import React, { useEffect, useState } from "react";
import MainContainer from "../UI/MainContainer";
import { useNavigate } from "react-router-dom";
import axios from "../../store/api/axios";
import { BsArrowRight } from "react-icons/bs";
import { PiMicrophone, PiSlidersHorizontal, PiUploadSimple } from "react-icons/pi";
import ProjectCard from "../UI/ProjectCard";
import SkeletonCard from "../UI/SkeletonCard";
import EmptyState from "../UI/EmptyState";
import { Post } from "../../types/Types";
import Button from "../UI/Button";
import { ButtonTypes, DEFAULT_AUTH_DATA } from "../../utils/Constants";
import usePageTitle from "../../utils/usePageTitle";

const STEPS = [
  {
    icon: <PiUploadSimple />,
    title: "Post a track",
    text: "Upload your recording, add sheet music, and pick the instruments you need.",
  },
  {
    icon: <PiMicrophone />,
    title: "Musicians add their audio",
    text: "They record over your track in the browser, in time with it.",
  },
  {
    icon: <PiSlidersHorizontal />,
    title: "Mix and download",
    text: "Pick the audio collaborations you like and download one file with everything in sync.",
  },
];

const Home: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null
  );
  const [postsList, setPostsList] = useState<Post[]>([]);
  // Without this, "No Post is Available!" flashed on every visit until the
  // posts arrived.
  const [isLoading, setIsLoading] = useState(true);
  const authData = localStorage.getItem("auth_data") ? JSON.parse(localStorage.getItem("auth_data")!) : DEFAULT_AUTH_DATA;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get("/posts/");
        const postListData: Post[] = response.data;
        const updatedPostListData = postListData.map((post) => {
          if (post.mp3_file && post.mp3_file.startsWith(`${process.env.BASE_URL}`)) { //????
            post.mp3_file = post.mp3_file.replace(`${process.env.BASE_URL}`, "/");
          }
          return post;
        });
        setPostsList(updatedPostListData);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleAudioPlay = (audio: HTMLAudioElement) => {
    if (currentAudio && currentAudio !== audio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    setCurrentAudio(audio);
  };

  const startProject = () => (authData?.id ? navigate("/post") : navigate("/login"));

  return (
    <MainContainer>
      <div className="flex flex-col h-full bg-white my-4">
        <section className="grid 768:grid-cols-[1.1fr_1fr] gap-8 items-center py-10 768:py-16">
          <div>
            <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#526554]">
              Music collaboration, at your own pace
            </p>
            <h1 className="mt-2 mb-4 480:text-5xl text-4xl font-bold leading-tight text-gray-800 text-balance">
              Make music together, from anywhere.
            </h1>
            <p className="text-lg text-gray-600 max-w-[48ch] mb-6">
              Post a track, let other musicians record their audio over it, then mix everything in sync and download it.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button type={ButtonTypes.PRIMARY} onClick={startProject} className="font-bold flex">
                Start a project
                <BsArrowRight className="ml-2 my-auto text-xl" />
              </Button>
              <Button
                type={ButtonTypes.DEFAULT}
                onClick={() => navigate("/projects")}
                className="font-bold px-8 py-4 text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5"
              >
                Browse projects
              </Button>
            </div>
          </div>
          <img src="/Images/Logo.jpg" alt="" className="w-full h-auto" />
        </section>

        <section aria-labelledby="how-it-works" className="bg-secondary rounded-3xl p-6">
          <h2 id="how-it-works" className="sr-only">How it works</h2>
          <ol className="grid 768:grid-cols-3 gap-4">
            {STEPS.map((step, index) => (
              <li key={step.title} className="bg-white rounded-[20px] shadow-md px-[18px] py-4 flex flex-col gap-1.5">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-xl bg-primary/[0.07] text-primary grid place-items-center text-xl">
                    {step.icon}
                  </span>
                  <span className="text-xs font-bold tracking-wide text-gray-70">STEP {index + 1}</span>
                </div>
                <h3 className="font-semibold">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <div className="w-full my-20">
          <div className="flex justify-between my-4">
            <h2 className="text-3xl font-bold my-auto">Recent projects</h2>
            <Button type={ButtonTypes.PRIMARY} onClick={() => navigate("/projects")} className="flex">
              See all
              <BsArrowRight className="ml-2 my-auto text-xl" />
            </Button>
          </div>
          {isLoading ? (
            <div className="grid grid-cols-1 920:grid-cols-2 1200:grid-cols-3 gap-5 bg-secondary p-5 rounded-3xl mx-auto w-full">
              <span className="sr-only" role="status">Loading projects…</span>
              {[0, 1, 2].map((i) => (
                <SkeletonCard key={i} className="h-[380px]" />
              ))}
            </div>
          ) : postsList.length > 0 ? (
            <div className="grid grid-cols-1 920:grid-cols-2 1200:grid-cols-3 gap-5 bg-secondary p-5 rounded-3xl mx-auto w-full">
              {postsList.slice(0, 3).map((post) => (
                <ProjectCard key={post.id} post={post} onAudioPlay={handleAudioPlay} />
              ))}
            </div>
          ) : (
            <EmptyState title="No projects yet" text="Start the first one and invite musicians to add their audio.">
              <Button type={ButtonTypes.PRIMARY} onClick={startProject} className="font-bold">
                Start a project
              </Button>
            </EmptyState>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default Home;
